import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import {
  Plus,
  Search,
  FileText,
  Calendar,
  DollarSign,
  MoreVertical,
  Edit,
  Trash2,
  Upload,
  CheckCircle,
  Clock,
  RefreshCw,
  Filter,
  Eye
} from 'lucide-react';
import { contratoService, formatCurrency, formatDate } from '@/services';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

const Contratos = () => {
  const { toast } = useToast();

  // Estados principais
  const [contratos, setContratos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos');
  const [stats, setStats] = useState(null);

  // Estados de modais
  const [modalOpen, setModalOpen] = useState(false);
  const [detalhesModalOpen, setDetalhesModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contratoSelecionado, setContratoSelecionado] = useState(null);
  const [modoEdicao, setModoEdicao] = useState(false);

  // Estados do formulário
  const [formData, setFormData] = useState({
    cliente_id: '',
    cliente_nome: '',
    titulo: '',
    descricao: '',
    valor_mensal: '',
    valor_setup: '',
    data_inicio: '',
    data_termino: '',
    dia_vencimento: '10',
    metodo_cobranca: 'boleto',
    tipo_recorrencia: 'mensal',
    gerar_fatura_automatica: true,
    dias_antecedencia_geracao: '5',
    observacoes: ''
  });

  // Upload de anexos
  const [uploadingAnexo, setUploadingAnexo] = useState(false);

  useEffect(() => {
    loadContratos();
    loadStats();
  }, []);

  const loadContratos = async () => {
    setLoading(true);
    try {
      const data = await contratoService.getAll();
      setContratos(data.contratos || []);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await contratoService.getStats();
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validações
      if (!formData.cliente_nome || !formData.titulo || !formData.valor_mensal) {
        toast({
          title: 'Erro',
          description: 'Preencha todos os campos obrigatórios',
          variant: 'destructive',
        });
        return;
      }

      // Preparar dados
      const dados = {
        ...formData,
        valor_mensal: parseFloat(formData.valor_mensal),
        valor_setup: formData.valor_setup ? parseFloat(formData.valor_setup) : 0,
        dia_vencimento: parseInt(formData.dia_vencimento),
        dias_antecedencia_geracao: parseInt(formData.dias_antecedencia_geracao),
      };

      // Remover campos vazios que podem causar erro de validação
      if (!dados.cliente_id) delete dados.cliente_id;
      if (!dados.data_termino) delete dados.data_termino;
      if (!dados.observacoes) delete dados.observacoes;

      if (modoEdicao && contratoSelecionado && contratoSelecionado._id) {
        // Atualizar
        await contratoService.update(contratoSelecionado._id, dados);
        toast({
          title: 'Sucesso',
          description: 'Contrato atualizado com sucesso',
        });
      } else if (!modoEdicao) {
        // Criar
        await contratoService.create(dados);
        toast({
          title: 'Sucesso',
          description: 'Contrato criado com sucesso',
        });
      }

      setModalOpen(false);
      resetForm();
      loadContratos();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (contrato) => {
    setContratoSelecionado(contrato);
    setModoEdicao(true);
    setFormData({
      cliente_id: contrato.cliente_id || '',
      cliente_nome: contrato.cliente_nome || '',
      titulo: contrato.titulo || '',
      descricao: contrato.descricao || '',
      valor_mensal: contrato.valor_mensal?.toString() || '',
      valor_setup: contrato.valor_setup?.toString() || '0',
      data_inicio: contrato.data_inicio?.split('T')[0] || '',
      data_termino: contrato.data_termino?.split('T')[0] || '',
      dia_vencimento: contrato.dia_vencimento?.toString() || '10',
      metodo_cobranca: contrato.metodo_cobranca || 'boleto',
      tipo_recorrencia: contrato.tipo_recorrencia || 'mensal',
      gerar_fatura_automatica: contrato.gerar_fatura_automatica !== false,
      dias_antecedencia_geracao: contrato.dias_antecedencia_geracao?.toString() || '5',
      observacoes: contrato.observacoes || ''
    });
    setModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await contratoService.delete(contratoSelecionado._id);
      toast({
        title: 'Sucesso',
        description: 'Contrato cancelado com sucesso',
      });
      setDeleteDialogOpen(false);
      setContratoSelecionado(null);
      loadContratos();
      loadStats();
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleGerarFatura = async (contrato) => {
    try {
      const contratoId = contrato.id || contrato._id;
      if (!contratoId) {
        toast({
          title: 'Erro',
          description: 'ID do contrato não encontrado',
          variant: 'destructive',
        });
        return;
      }
      
      await contratoService.gerarFatura(contratoId);
      toast({
        title: 'Sucesso',
        description: 'Fatura gerada com sucesso',
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleUploadAnexo = async (contratoId, file) => {
    setUploadingAnexo(true);
    try {
      await contratoService.uploadAnexo(contratoId, file);
      toast({
        title: 'Sucesso',
        description: 'Anexo enviado com sucesso',
      });
      // Recarregar detalhes do contrato
      const detalhes = await contratoService.getById(contratoId);
      setContratoSelecionado(detalhes);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    } finally {
      setUploadingAnexo(false);
    }
  };

  const handleDeleteAnexo = async (contratoId, anexoId) => {
    try {
      await contratoService.deleteAnexo(contratoId, anexoId);
      toast({
        title: 'Sucesso',
        description: 'Anexo removido com sucesso',
      });
      // Recarregar detalhes
      const detalhes = await contratoService.getById(contratoId);
      setContratoSelecionado(detalhes);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const handleVerDetalhes = async (contrato) => {
    try {
      const detalhes = await contratoService.getById(contrato._id);
      setContratoSelecionado(detalhes);
      setDetalhesModalOpen(true);
    } catch (error) {
      toast({
        title: 'Erro',
        description: error,
        variant: 'destructive',
      });
    }
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      cliente_nome: '',
      titulo: '',
      descricao: '',
      valor_mensal: '',
      valor_setup: '',
      data_inicio: '',
      data_termino: '',
      dia_vencimento: '10',
      metodo_cobranca: 'boleto',
      tipo_recorrencia: 'mensal',
      gerar_fatura_automatica: true,
      dias_antecedencia_geracao: '5',
      observacoes: ''
    });
    setModoEdicao(false);
    setContratoSelecionado(null);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ativo: { label: 'Ativo', className: 'bg-green-100 text-green-800 hover:bg-green-100' },
      suspenso: { label: 'Suspenso', className: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100' },
      cancelado: { label: 'Cancelado', className: 'bg-red-100 text-red-800 hover:bg-red-100' },
      finalizado: { label: 'Finalizado', className: 'bg-gray-100 text-gray-800 hover:bg-gray-100' }
    };

    const config = statusConfig[status] || statusConfig.ativo;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  // Filtrar contratos
  const contratosFiltrados = contratos.filter(contrato => {
    const matchSearch = contrato.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       contrato.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       contrato.numero_contrato?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatus = filtroStatus === 'todos' || contrato.status === filtroStatus;
    
    return matchSearch && matchStatus;
  });

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-purple-600" />
          <p className="text-gray-500">Carregando contratos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Contratos</h1>
          <p className="text-gray-500 mt-1">Gerencie os contratos de clientes</p>
        </div>
        <Button onClick={() => { resetForm(); setModalOpen(true); }}>
          <Plus className="h-4 w-4 mr-2" />
          Novo Contrato
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total de Contratos</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_contratos}</p>
                </div>
                <FileText className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Contratos Ativos</p>
                  <p className="text-2xl font-bold text-green-600">{stats.contratos_ativos}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Valor Mensal Total</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(stats.valor_total_mensal)}
                  </p>
                </div>
                <DollarSign className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Renovações (90d)</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.proximos_renovacao_90d}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por cliente, título ou número do contrato..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filtroStatus} onValueChange={setFiltroStatus}>
              <SelectTrigger className="w-full md:w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os Status</SelectItem>
                <SelectItem value="ativo">Ativos</SelectItem>
                <SelectItem value="suspenso">Suspensos</SelectItem>
                <SelectItem value="cancelado">Cancelados</SelectItem>
                <SelectItem value="finalizado">Finalizados</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={loadContratos}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Lista de Contratos */}
      {contratosFiltrados.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FileText className="h-16 w-16 text-gray-300 mb-4" />
            <p className="text-gray-500 text-lg font-medium">Nenhum contrato encontrado</p>
            <p className="text-gray-400 text-sm mt-2">
              {searchTerm || filtroStatus !== 'todos' 
                ? 'Tente ajustar os filtros'
                : 'Crie seu primeiro contrato clicando no botão acima'
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {contratosFiltrados.map((contrato) => (
            <Card key={contrato.id} className="hover:shadow-lg transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CardTitle className="text-lg">{contrato.titulo}</CardTitle>
                      {getStatusBadge(contrato.status)}
                    </div>
                    <CardDescription className="flex items-center gap-2">
                      <span className="font-mono text-xs">{contrato.numero_contrato}</span>
                      <span>•</span>
                      <span>{contrato.cliente_nome}</span>
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleVerDetalhes(contrato)}>
                        <Eye className="h-4 w-4 mr-2" />
                        Ver Detalhes
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleEdit(contrato)}>
                        <Edit className="h-4 w-4 mr-2" />
                        Editar
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleGerarFatura(contrato)}>
                        <FileText className="h-4 w-4 mr-2" />
                        Gerar Fatura
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setContratoSelecionado(contrato);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Cancelar
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500">Valor Mensal</p>
                    <p className="text-lg font-bold text-gray-900">
                      {formatCurrency(contrato.valor_mensal)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Dia de Vencimento</p>
                    <p className="text-lg font-semibold text-gray-700">
                      Dia {contrato.dia_vencimento}
                    </p>
                  </div>
                </div>

                <div className="pt-3 border-t">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-gray-600">
                      <Calendar className="h-4 w-4" />
                      <span>Início: {formatDate(contrato.data_inicio)}</span>
                    </div>
                    {contrato.data_proxima_renovacao && (
                      <div className="flex items-center gap-2 text-yellow-600">
                        <Clock className="h-4 w-4" />
                        <span>Renova: {formatDate(contrato.data_proxima_renovacao)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {contrato.observacoes && (
                  <div className="pt-2 border-t">
                    <p className="text-xs text-gray-500 line-clamp-2">{contrato.observacoes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal de Criar/Editar Contrato */}
      <Dialog open={modalOpen} onOpenChange={(open) => {
        setModalOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {modoEdicao ? 'Editar Contrato' : 'Novo Contrato'}
            </DialogTitle>
            <DialogDescription>
              {modoEdicao ? 'Atualize as informações do contrato' : 'Preencha os dados do novo contrato'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Cliente */}
            <div className="space-y-2">
              <Label htmlFor="cliente_nome">Cliente *</Label>
              <Input
                id="cliente_nome"
                value={formData.cliente_nome}
                onChange={(e) => setFormData({ ...formData, cliente_nome: e.target.value })}
                placeholder="Nome do cliente"
                required
              />
            </div>

            {/* Título */}
            <div className="space-y-2">
              <Label htmlFor="titulo">Título do Contrato *</Label>
              <Input
                id="titulo"
                value={formData.titulo}
                onChange={(e) => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Ex: Contrato de Suporte Mensal"
                required
              />
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição</Label>
              <Textarea
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Detalhes do contrato"
                rows={3}
              />
            </div>

            {/* Valores */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="valor_mensal">Valor Mensal *</Label>
                <Input
                  id="valor_mensal"
                  type="number"
                  step="0.01"
                  value={formData.valor_mensal}
                  onChange={(e) => setFormData({ ...formData, valor_mensal: e.target.value })}
                  placeholder="0.00"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor_setup">Valor de Setup</Label>
                <Input
                  id="valor_setup"
                  type="number"
                  step="0.01"
                  value={formData.valor_setup}
                  onChange={(e) => setFormData({ ...formData, valor_setup: e.target.value })}
                  placeholder="0.00"
                />
              </div>
            </div>

            {/* Datas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="data_inicio">Data de Início *</Label>
                <Input
                  id="data_inicio"
                  type="date"
                  value={formData.data_inicio}
                  onChange={(e) => setFormData({ ...formData, data_inicio: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="data_termino">Data de Término</Label>
                <Input
                  id="data_termino"
                  type="date"
                  value={formData.data_termino}
                  onChange={(e) => setFormData({ ...formData, data_termino: e.target.value })}
                />
              </div>
            </div>

            {/* Configurações de Cobrança */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dia_vencimento">Dia de Vencimento *</Label>
                <Select
                  value={formData.dia_vencimento}
                  onValueChange={(value) => setFormData({ ...formData, dia_vencimento: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(31)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>
                        Dia {i + 1}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="metodo_cobranca">Método de Cobrança</Label>
                <Select
                  value={formData.metodo_cobranca}
                  onValueChange={(value) => setFormData({ ...formData, metodo_cobranca: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="boleto">Boleto</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                    <SelectItem value="cartao_credito">Cartão de Crédito</SelectItem>
                    <SelectItem value="transferencia">Transferência</SelectItem>
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tipo_recorrencia">Recorrência</Label>
                <Select
                  value={formData.tipo_recorrencia}
                  onValueChange={(value) => setFormData({ ...formData, tipo_recorrencia: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mensal">Mensal</SelectItem>
                    <SelectItem value="trimestral">Trimestral</SelectItem>
                    <SelectItem value="semestral">Semestral</SelectItem>
                    <SelectItem value="anual">Anual</SelectItem>
                    <SelectItem value="unico">Único</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Observações */}
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                placeholder="Notas internas sobre o contrato"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModalOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {modoEdicao ? 'Atualizar' : 'Criar'} Contrato
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Modal de Detalhes */}
      <Dialog open={detalhesModalOpen} onOpenChange={setDetalhesModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span>{contratoSelecionado?.titulo}</span>
              {contratoSelecionado && getStatusBadge(contratoSelecionado.status)}
            </DialogTitle>
            <DialogDescription>
              {contratoSelecionado?.numero_contrato} • {contratoSelecionado?.cliente_nome}
            </DialogDescription>
          </DialogHeader>

          {contratoSelecionado && (
            <div className="space-y-6">
              {/* Informações Principais */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Valor Mensal</Label>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatCurrency(contratoSelecionado.valor_mensal)}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Dia de Vencimento</Label>
                  <p className="text-2xl font-bold text-gray-900">
                    Dia {contratoSelecionado.dia_vencimento}
                  </p>
                </div>
              </div>

              {/* Datas */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-gray-500">Data de Início</Label>
                  <p className="font-medium">{formatDate(contratoSelecionado.data_inicio)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Data de Término</Label>
                  <p className="font-medium">
                    {contratoSelecionado.data_termino ? formatDate(contratoSelecionado.data_termino) : 'Indeterminado'}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Próxima Renovação</Label>
                  <p className="font-medium">{formatDate(contratoSelecionado.data_proxima_renovacao)}</p>
                </div>
              </div>

              {/* Configurações */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="text-gray-500">Método de Cobrança</Label>
                  <p className="font-medium capitalize">{contratoSelecionado.metodo_cobranca?.replace('_', ' ')}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Recorrência</Label>
                  <p className="font-medium capitalize">{contratoSelecionado.tipo_recorrencia}</p>
                </div>
              </div>

              {/* Descrição e Observações */}
              {contratoSelecionado.descricao && (
                <div>
                  <Label className="text-gray-500">Descrição</Label>
                  <p className="text-gray-700 mt-1">{contratoSelecionado.descricao}</p>
                </div>
              )}

              {contratoSelecionado.observacoes && (
                <div>
                  <Label className="text-gray-500">Observações</Label>
                  <p className="text-gray-700 mt-1">{contratoSelecionado.observacoes}</p>
                </div>
              )}

              {/* Faturas Recentes */}
              {contratoSelecionado.faturas_recentes && contratoSelecionado.faturas_recentes.length > 0 && (
                <div>
                  <Label className="text-gray-500 mb-2 block">Faturas Recentes ({contratoSelecionado.total_faturas} total)</Label>
                  <div className="space-y-2">
                    {contratoSelecionado.faturas_recentes.slice(0, 5).map((fatura) => (
                      <div key={fatura._id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium">{fatura.numero_fatura}</p>
                          <p className="text-sm text-gray-500">Venc: {formatDate(fatura.data_vencimento)}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold">{formatCurrency(fatura.valor_final)}</p>
                          <Badge variant="secondary" className="mt-1">{fatura.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Anexos */}
              <div>
                <Label className="text-gray-500 mb-2 block">Anexos</Label>
                
                {/* Lista de anexos */}
                {contratoSelecionado.anexos && contratoSelecionado.anexos.length > 0 && (
                  <div className="space-y-2 mb-4">
                    {contratoSelecionado.anexos.map((anexo) => (
                      <div key={anexo.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-gray-500" />
                          <span className="text-sm">{anexo.nome_arquivo}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteAnexo(contratoSelecionado.id || contratoSelecionado._id, anexo.id || anexo._id)}
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    id="file-upload"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        handleUploadAnexo(contratoSelecionado.id || contratoSelecionado._id, e.target.files[0]);
                      }
                    }}
                    disabled={uploadingAnexo}
                  />
                  <label
                    htmlFor="file-upload"
                    className="flex flex-col items-center justify-center cursor-pointer"
                  >
                    {uploadingAnexo ? (
                      <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mb-2" />
                    ) : (
                      <Upload className="h-8 w-8 text-gray-400 mb-2" />
                    )}
                    <p className="text-sm text-gray-600">
                      {uploadingAnexo ? 'Enviando...' : 'Clique para fazer upload de arquivo'}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, XLS, XLSX</p>
                  </label>
                </div>
              </div>

              {/* Ações */}
              <DialogFooter>
                <Button variant="outline" onClick={() => setDetalhesModalOpen(false)}>
                  Fechar
                </Button>
                <Button onClick={() => { setDetalhesModalOpen(false); handleEdit(contratoSelecionado); }}>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </Button>
                <Button onClick={() => handleGerarFatura(contratoSelecionado)}>
                  <FileText className="h-4 w-4 mr-2" />
                  Gerar Fatura
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação cancelará o contrato <strong>{contratoSelecionado?.numero_contrato}</strong>.
              O contrato será marcado como cancelado e não poderá ser reativado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Sim, Cancelar Contrato
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Contratos;
